import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LikeFilled } from '@ant-design/icons';

// Akış fotoğraflarının ortak davranışları: dik algılama, yumuşak belirme, çift dokunma ve kalp.

const DOUBLE_TAP_MS = 280;

/**
 * Bilinen oran (yüklemede kaydedilir) dik fotoğrafı ilk çizimde kare alanla açar; eski fotoğraflarda
 * yükleme sonrası algılanır. Yalnız istemcide henüz yüklenmemiş görsel soluk başlar: SSR ile gelen
 * ya da önbellekten anında yüklenen görsel hiç gizlenmez.
 */
export function usePhotoState(ratio?: number) {
  const [portrait, setPortrait] = useState(!!ratio && ratio < 1);
  const [phase, setPhase] = useState<'server' | 'loading' | 'loaded'>('server');
  useEffect(() => setPhase(current => (current === 'server' ? 'loading' : current)), []);
  const loaded = useCallback((image: HTMLImageElement) => {
    setPortrait(image.naturalHeight > image.naturalWidth);
    setPhase('loaded');
  }, []);
  const imageRef = useCallback(
    (image: HTMLImageElement | null) => {
      if (image?.complete && image.naturalWidth) loaded(image);
    },
    [loaded],
  );
  return {
    portrait,
    loading: phase === 'loading',
    imageRef,
    onLoad: (event: React.SyntheticEvent<HTMLImageElement>) => loaded(event.currentTarget),
    reset: () => setPhase('loading'),
  };
}

/** Tek dokunma kısa beklemeyle `single`, ikinci dokunma `double` çalıştırır. */
export function useDoubleTap(single: (() => void) | undefined, double: (() => void) | undefined) {
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  return () => {
    if (!double) {
      single?.();
      return;
    }
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
      double();
      return;
    }
    timer.current = window.setTimeout(() => {
      timer.current = null;
      single?.();
    }, DOUBLE_TAP_MS);
  };
}

/** Onaylanan tebrikte fotoğrafın ortasında kısa kalp; bilgi taşımaz (düğme durumu asıl kaynak). */
export function PhotoBurst({ burst }: { burst?: number }) {
  if (!burst) return null;
  return (
    <span key={burst} className="photo-burst" aria-hidden="true">
      <LikeFilled />
    </span>
  );
}

PhotoBurst.defaultProps = { burst: undefined };
